import { Card, Checkbox, Form, FormInstance, Radio, Select, Space, Typography } from 'antd';
import Title from 'antd/lib/typography/Title';

import IpListField, { IpFilterFormItem } from '../IpListField';

/** 多要素認証を設定可能にするユーザーの権限オプション */
const MFA_ROLE_OPTIONS = [
    { label: '全体管理者権限', value: 'administrator' },
    { label: '学校管理者権限', value: 'school_administrator' },
    { label: '教員権限', value: 'teacher' },
    { label: '児童生徒権限', value: 'student' },
];

/** 再要求するまでの日数オプション (0〜3日) */
const DAY_OPTIONS = Array.from({ length: 4 }, (_, i) => ({
    label: `${i}日`,
    value: i,
}));

/** 再要求するまでの時間数オプション (0〜23時間) */
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
    label: `${i}時間`,
    value: i,
}));

/** フォーム値の型 */
export type MfaSectionFormValues = {
    mfaEnabled: boolean;
    mfaAllowedRoles: string[];
    mfaSkipSameDevice: boolean;
    mfaSkipDays: number;
    mfaSkipHours: number;
    mfaIpRestrictionEnabled: boolean;
    mfaIpFilters: IpFilterFormItem[];
};

interface MfaSectionProps {
    /** 親フォームのインスタンス */
    form: FormInstance;
    /** ローディング・無効化状態 */
    disabled?: boolean;
    /** ラベルなしのオフセットレイアウト */
    layoutWithoutLabel: object;
    /** 追加ボタンのレイアウト */
    layoutWithAddButton: object;
}

/**
 * 多要素認証の有効化セクション
 * MFA 設定・対象ユーザー・スキップ条件・IP 制限を管理する
 */
const MfaSection: React.FC<MfaSectionProps> = ({
    form,
    disabled = false,
    layoutWithoutLabel,
    layoutWithAddButton,
}) => {
    // 「指定した時間が経過するまで多要素認証を再要求しない」のチェック状態
    const mfaSkipSameDevice: boolean = Form.useWatch('mfaSkipSameDevice', form) ?? false;

    // 「0日」+「0時間」の組み合わせを選択できないよう、片方が 0 の間はもう片方の 0 を無効化する
    // (backend 側は expire_mfa_hour の min=1 バリデーション済み: LGT-4683)
    const mfaSkipDays: number = Form.useWatch('mfaSkipDays', form) ?? 0;
    const mfaSkipHours: number = Form.useWatch('mfaSkipHours', form) ?? 0;
    const dayOptions = DAY_OPTIONS.map((option) =>
        option.value === 0 && mfaSkipHours === 0 ? { ...option, disabled: true } : option
    );
    const hourOptions = HOUR_OPTIONS.map((option) =>
        option.value === 0 && mfaSkipDays === 0 ? { ...option, disabled: true } : option
    );

    return (
        <Card title={<Title level={4}>多要素認証の有効化</Title>} type='inner'>

            <Typography.Title level={5}>
                この機能を有効化することで、L-Gateにアクセスする時や、教材・アプリを起動する時の多要素認証を設定できるようになります。
            </Typography.Title>

            {/* 多要素認証機能を有効化する */}
            <Form.Item label='多要素認証機能を有効化する' name='enableMfa'>
                <Radio.Group disabled={disabled}>
                    <Radio value={true}>有効</Radio>
                    <Radio value={false}>無効</Radio>
                </Radio.Group>
            </Form.Item>

            {/* 認証アプリを登録できるユーザー */}
            <Form.Item label='認証アプリを登録できるユーザー' name='requireMfaPermission'>
                <Checkbox.Group disabled={disabled} options={MFA_ROLE_OPTIONS} />
            </Form.Item>

            {/* 多要素認証の再要求 (ログインのたびに要求 / 一定期間スキップ)。
                mfaSkipSameDevice=true が「最後の認証から指定期間内は要求しない」に対応する。
                選択肢の文言が長いため縦並びにする (LGT-4758) */}
            <Form.Item
                label='多要素認証の再要求'
                tooltip='同一ユーザーが同じブラウザでログインする際に適用されます'
                name='mfaSkipSameDevice'
            >
                <Radio.Group disabled={disabled}>
                    <Space direction='vertical'>
                        <Radio value={false}>ログインのたびに要求する</Radio>
                        <Radio value={true}>最後の認証から指定期間内は要求しない</Radio>
                    </Space>
                </Radio.Group>
            </Form.Item>

            {/* 再要求までの期間 (「ログインのたびに要求する」を選択している間は非表示。
                hidden で値は保持し、送信値 expire_mfa_hour の計算に使い続ける) */}
            <Form.Item
                label='再要求までの期間'
                tooltip='最後に多要素認証に成功した時点から指定期間が経過するまで、再ログイン時の多要素認証を省略します。セッション継続中は再要求されません'
                hidden={!mfaSkipSameDevice}
            >
                <Form.Item name='mfaSkipDays' noStyle>
                    <Select
                        disabled={disabled}
                        options={dayOptions}
                        style={{ width: 90 }}
                    />
                </Form.Item>
                <Form.Item name='mfaSkipHours' noStyle>
                    <Select
                        disabled={disabled}
                        options={hourOptions}
                        style={{ marginLeft: 8, width: 100 }}
                    />
                </Form.Item>
            </Form.Item>

            {/* 認証アプリを登録・無効化できるIPアドレスを制限する (「制限しない」を選択してもリストの値は保持される) */}
            <Form.Item label='認証アプリを登録・無効化できるIPアドレスを制限する' name='mfaAllowIpsEnabled'>
                <Radio.Group disabled={disabled}>
                    <Radio value={false}>制限しない</Radio>
                    <Radio value={true}>制限する</Radio>
                </Radio.Group>
            </Form.Item>

            {/* IP アドレスリスト (制限する/制限しないに関わらず常時表示する) */}
            <IpListField
                form={form}
                layoutWithAddButton={layoutWithAddButton}
                layoutWithoutLabel={layoutWithoutLabel}
                listName='mfaAllowedIps'
            />
        </Card>
    );
};

export default MfaSection;
